import { env } from "@/lib/utils";
import type { ApiResponse } from "@/types/common";
import type {
  GetSubscriptionResponse,
  SubscribedUser,
  SubscriptionPlan,
} from "@/types/subscription";
import { privateRequest } from "../axios";

export interface RazorpayOrder {
  id: string;
  amount: number;
  currency: string;
  receipt?: string;
}

export interface RazorpayOrderPayload {
  amount: number;
  currency: string;
  receipt: string;
  planId: number | string;
  notes?: Record<string, string>;
}

export interface OrderInitiatePayload {
  planId: number | string;
  paymentProvider: string;
}

export interface ChangePlanPayload {
  planId: number | string;
  immediate: boolean;
}
export interface SubscriptionCallbackPayload {
  razorpay_order_id: string;
  razorpay_payment_id: string;
  razorpay_signature: string;
}

export interface RazorpayOrderResponse {
  order?: RazorpayOrder;
  id?: string;
  amount?: number;
  currency?: string;
  receipt?: string;
}

export interface RazorpayVerificationPayload extends RazorpayHandlerResponse {
  planId: number | string;
}

export interface RazorpayVerificationResponse {
  verified?: boolean;
  subscription?: unknown;
}

const ENDPOINTS = {
  CREATE_ORDER: env("VITE_RAZORPAY_ORDER_ENDPOINT", "/payments/razorpay/order"),
  VERIFY_PAYMENT: env(
    "VITE_RAZORPAY_VERIFY_ENDPOINT",
    "/payments/razorpay/verify",
  ),
  ORDER_INITIATE: "subscription/initiate",
  ME_INITIATE: "subscription/me",
  GET_SUBSCRIPTION_PLANS: "subscription-plan",
  SUBSCRIPTION_CALLBACK: "subscription/callback",
  CANCEL_SUBSCRIPTION: "subscription/razorpay/subscription/cancel",
  CHANGE_PLAN: "subscription/razorpay/subscription/change-plan",
};

const createOrder = async (
  payload: RazorpayOrderPayload,
): Promise<ApiResponse<RazorpayOrderResponse>> => {
  const response = await privateRequest.post<ApiResponse<RazorpayOrderResponse>>(
    ENDPOINTS.CREATE_ORDER,
    payload,
  );

  return response.data;
};

const orderInitiate = async (payload: OrderInitiatePayload) => {
  const response = await privateRequest.post(ENDPOINTS.ORDER_INITIATE, payload);
  return response.data;
}

const subscriptionCallback = async (payload: SubscriptionCallbackPayload) => {
  const formData = new URLSearchParams();
  formData.append("razorpay_order_id", payload.razorpay_order_id);
  formData.append("razorpay_payment_id", payload.razorpay_payment_id);
  formData.append("razorpay_signature", payload.razorpay_signature);

  const response = await privateRequest.post(
    ENDPOINTS.SUBSCRIPTION_CALLBACK,
    formData,
    {
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
      },
    },
  );
  return response.data;
};

const getSubscriptionPlans = async (): Promise<
  ApiResponse<GetSubscriptionResponse<SubscriptionPlan>>
> => {
  const response = await privateRequest.get<
    ApiResponse<GetSubscriptionResponse<SubscriptionPlan>>
  >(ENDPOINTS.GET_SUBSCRIPTION_PLANS);
  return response.data;
};

const mySubscriptions = async (): Promise<ApiResponse<SubscribedUser | null>> => {
  const response = await privateRequest.get<ApiResponse<SubscribedUser | null>>(
    ENDPOINTS.ME_INITIATE,
  );
  return response.data;
};

const cancelSubscription = async (
  immediate: boolean,
): Promise<ApiResponse<unknown>> => {
  const response = await privateRequest.post<ApiResponse<unknown>>(
    ENDPOINTS.CANCEL_SUBSCRIPTION,
    null,
    { params: { immediate } },
  );
  return response.data;
};

const changePlan = async (
  payload: ChangePlanPayload,
): Promise<ApiResponse<unknown>> => {
  const response = await privateRequest.post<ApiResponse<unknown>>(
    ENDPOINTS.CHANGE_PLAN,
    payload,
  );
  return response.data;
};

const verifyPayment = async (
  payload: RazorpayVerificationPayload,
): Promise<ApiResponse<RazorpayVerificationResponse>> => {
  const response = await privateRequest.post<
    ApiResponse<RazorpayVerificationResponse>
  >(ENDPOINTS.VERIFY_PAYMENT, payload);

  return response.data;
};

const RazorpayService = {
  createOrder,
  verifyPayment,
  orderInitiate,
  getSubscriptionPlans,
  mySubscriptions,
  subscriptionCallback,
  cancelSubscription,
  changePlan,
};

export default RazorpayService;
